import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { OrdinarySpuItem, PackageSpuItem } from '../spu.config';
import {
  SpuApiService,
  SpuListResponse,
  SpuSearchParams,
} from './spu-api.service';

export interface SearchFormData {
  name?: string;
  spuCode?: string;
  type?: string;
  categoryIdList?: string | string[] | any[]; // Allow array of objects from treeselect
  brandId?: string;
  materialName?: string;
  status?: boolean;
}

export interface SpuCreateUpdateResponse {
  response: any;
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class SpuService {
  private currentSpuType: 'ordinary' | 'package' = 'ordinary';

  constructor(private spuApiService: SpuApiService) {}

  // Set SPU type for API calls
  setSpuType(spuType: 'ordinary' | 'package'): void {
    this.currentSpuType = spuType;
  }

  // Get SPU list with pagination and search - following brand pattern
  getSpus(
    params: any,
  ): Observable<SpuListResponse<OrdinarySpuItem | PackageSpuItem>> {
    const {
      name,
      spuCode,
      type,
      categoryIdList,
      brandId,
      materialName,
      status,
      page = 1,
      size = 20,
    } = params;
    // Convert search parameters to API format
    const searchParams: SpuSearchParams = {
      name: name?.trim(),
      spuCode: spuCode?.trim(),
      type: type,
      categoryIdList: categoryIdList, // Map categoryIdList to categoryId for API
      brandId: brandId,
      materialName: materialName,
      status: status, // Keep status as is (null, undefined, true, false)
    };

    // Remove undefined and null values
    Object.keys(searchParams).forEach((key) => {
      const val = searchParams[key as keyof SpuSearchParams];
      if (val === undefined || val === null) {
        delete searchParams[key as keyof SpuSearchParams];
      }
    });

    const isPackage = this.currentSpuType === 'package';

    return this.spuApiService
      .getSpuList(page, size, searchParams, isPackage)
      .pipe(
        map((response: SpuListResponse<OrdinarySpuItem | PackageSpuItem>) => ({
          data: response.data || [],
          totalRecords: response.totalRecords || 0,
          loading: false,
        })),
        catchError((error) => {
          console.error('Error fetching SPUs:', error);
          throw error;
        }),
      );
  }

  // Get SPU by ID
  getSpuById(id: string): Observable<OrdinarySpuItem | PackageSpuItem | null> {
    const numericId = parseInt(id);
    return this.spuApiService.getSpuById(numericId).pipe(
      map((apiItem: any) => {
        if (!apiItem) return null;

        if (this.currentSpuType === 'ordinary') {
          return {
            id: apiItem.id?.toString() || '',
            spuCode: apiItem.spuCode || '',
            name: apiItem.name || '',
            type: apiItem.type || 'PHYSICAL',
            categoryName: apiItem.categoryName || '',
            brandName: apiItem.brandName,
            materialName: apiItem.materialName,
            remark: apiItem.remark || '',
            status: apiItem.status === 1,
            lastUpdateDate: apiItem.lastUpdateDate || apiItem.updatedAt || '',
          } as OrdinarySpuItem;
        } else {
          return {
            id: apiItem.id?.toString() || '',
            spuCode: apiItem.spuCode || '',
            name: apiItem.name || '',
            type: apiItem.type || 'PHYSICAL',
            categoryName: apiItem.categoryName || '',
            materialName: apiItem.materialName,
            remark: apiItem.remark || '',
            status: apiItem.status === 1,
            packageItems: apiItem.packageItems || [],
          } as PackageSpuItem;
        }
      }),
      catchError((error) => {
        console.error('Error fetching SPU by ID:', error);
        return of(null);
      }),
    );
  }

  // Update SPU status
  updateStatus(id: string, status: boolean): Observable<string> {
    const numericId = parseInt(id);
    return this.spuApiService.updateSpuStatus(numericId, status).pipe(
      map(() => 'Status updated successfully'),
      catchError((error) => {
        console.error('Error updating SPU status:', error);
        throw error;
      }),
    );
  }

  // Delete SPU
  deleteSpu(id: string): Observable<string> {
    const numericId = parseInt(id);
    return this.spuApiService.deleteSpu(numericId).pipe(
      map(() => 'Product deleted successfully'),
      catchError((error) => {
        console.error('Error deleting SPU:', error);
        throw error;
      }),
    );
  }

  // Create SPU - returns both response and message
  createSpu(spuData: any): Observable<SpuCreateUpdateResponse> {
    const isPackage = this.currentSpuType === 'package';
    return this.spuApiService.createSpu(spuData, isPackage).pipe(
      map((response: any) => ({
        response,
        message: `${isPackage ? 'Package' : 'Standard'} product created successfully`,
      })),
      catchError((error) => {
        console.error('Error creating SPU:', error);
        throw error;
      }),
    );
  }

  // Update SPU - returns both response and message
  updateSpu(spuData: any): Observable<SpuCreateUpdateResponse> {
    const isPackage = this.currentSpuType === 'package';
    return this.spuApiService.updateSpu(spuData, isPackage).pipe(
      map((response: any) => ({
        response,
        message: 'Product updated successfully',
      })),
      catchError((error) => {
        console.error('Error updating SPU:', error);
        throw error;
      }),
    );
  }
}
